<script>
import DynamicDataTable from '@/components/DynamicDataTable.vue';
import DeleteDialog from '@/components/DeleteDialog.vue';
import AntInput from '@/components/AntInput.vue';
import { mapGetters } from 'vuex';
import FileHandlerService from '@/services/file-handler';
import { phaseOptions } from '@/modules/daiwa-house-modular-europe/WkbLibrary/DhmeWkb.utils';
import DhmeWkbPhaseChip from '@/modules/daiwa-house-modular-europe/DhmeWkbPhaseChip.vue';

export default {
  name: 'DhmeWkbFormsLibrary',
  components: { DhmeWkbPhaseChip, AntInput, DeleteDialog, DynamicDataTable },
  data: () => {
    return {
      tableRecords: [],
      tableHeaders: [
        {
          text: 'Measurement',
          value: 'measurement_id',
          hasSlot: true,
        },
        {
          text: 'Leanforms form',
          value: 'lf_form',
        },
        {
          text: 'Phase',
          value: 'phase',
          hasSlot: true,
        },
        {
          text: 'Leanforms project',
          value: 'lf_project',
        },
        {
          text: 1,
          value: 'q1',
          hasSlot: true,
        },
        {
          text: 2,
          value: 'q2',
          hasSlot: true,
        },
        {
          text: 3,
          value: 'q3',
          hasSlot: true,
        },
        {
          text: 4,
          value: 'q4',
          hasSlot: true,
        },
        {
          text: 5,
          value: 'q5',
          hasSlot: true,
        },
        {
          text: 'BG',
          value: 'export_bg',
          hasSlot: true,
          width: '100px',
        },
        {
          text: 'Warranty',
          value: 'export_warranty',
          hasSlot: true,
          width: '100px',
        },
        {
          text: 'Contract',
          value: 'export_contract',
          hasSlot: true,
          width: '100px',
        },
        {
          text: '',
          value: 'actions',
          align: 'right',
        },
      ],
      wkbFormItem: {},
      wkbFormDialogDisplayed: false,
      wkbFormDeleteDialogDisplayed: false,
      rules: {
        required: (value) => !!value || 'Required.',
      },
      measurementFilter: '',
    };
  },
  computed: {
    ...mapGetters([
      'wkbLibraryMeasurements',
      'wkbLibraryMeasurementsForms',
      'wkbLibraryTableStatus',
      'project',
    ]),

    formMeasurements() {
      return this.wkbLibraryMeasurements
        .filter((m) => m.type === 'leanform')
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    filteredForms() {
      if (this.measurementFilter) {
        return this.wkbLibraryMeasurementsForms.filter(
          (f) => f.measurement_id === this.measurementFilter
        );
      }

      return this.wkbLibraryMeasurementsForms;
    },
  },
  methods: {
    phaseOptions() {
      return phaseOptions;
    },
    closeWKBFormDialog() {
      this.wkbFormItem = Object.assign({}, {});
      this.wkbFormDialogDisplayed = false;
      this.wkbFormDeleteDialogDisplayed = false;
      this.$refs['create-wkb-form-form'].reset();
    },
    setupEdit(record) {
      this.wkbFormItem = Object.assign({}, record);
      this.wkbFormDialogDisplayed = true;
    },
    setupDelete(record) {
      this.wkbFormItem = Object.assign({}, record);
      this.wkbFormDeleteDialogDisplayed = true;
    },
    async saveOrUpdateWKBForm() {
      if (this.$refs['create-wkb-form-form'].validate()) {
        if (this.wkbFormItem.id) {
          // update item
          const recordId = this.wkbFormItem.id;
          delete this.wkbFormItem.id;
          if (this.wkbFormItem.default instanceof File) {
            await FileHandlerService.handleFile(this.wkbFormItem.default).then(
              (value) => {
                this.wkbFormItem.default = value;
              }
            );
          }
          this.$store
            .dispatch('updateWkbLibraryRecord', {
              table: 'CFFA_DHME_WKB_MEASUREMENT_FORMS',
              recordId,
              body: {
                record: this.wkbFormItem,
              },
            })
            .then(() => {
              this.closeWKBFormDialog();
            });
        } else {
          // create item
          if (this.wkbFormItem.default instanceof File) {
            await FileHandlerService.handleFile(this.wkbFormItem.default).then(
              (value) => {
                this.wkbFormItem.default = value;
              }
            );
          }
          this.$store
            .dispatch('createWkbLibraryRecord', {
              table: 'CFFA_DHME_WKB_MEASUREMENT_FORMS',
              body: {
                record: this.wkbFormItem,
              },
            })
            .then(() => {
              this.closeWKBFormDialog();
            });
        }
      }
    },
    deleteWkbFormRecord() {
      this.$store
        .dispatch('deleteWkbLibraryRecord', {
          table: 'CFFA_DHME_WKB_MEASUREMENT_FORMS',
          recordId: this.wkbFormItem.id,
        })
        .then(() => {
          this.closeWKBFormDialog();
        });
    },
  },
};
</script>

<template>
  <div class="ant-glass-background radius-0">
    <dynamic-data-table
      :can-delete="true"
      :can-edit="true"
      :is-loading="wkbLibraryTableStatus === 'loading'"
      :project-id="project.id"
      :table-headers="tableHeaders"
      :table-records="filteredForms"
      table-title=""
      @deleteItem="setupDelete"
      @editItem="setupEdit"
    >
      <template #table-buttons>
        <v-autocomplete
          v-model="measurementFilter"
          :items="formMeasurements"
          clearable
          dense
          filled
          flat
          hide-details
          item-text="name"
          item-value="id"
          placeholder="Measurement"
          style="max-width: 300px !important"
        />
      </template>
      <template #table-actions>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon
              class="ant-icon"
              @click="wkbFormDialogDisplayed = true"
              v-on="on"
            >
              mdi-plus
            </v-icon>
          </template>
          <span>Add form</span>
        </v-tooltip>
      </template>
      <template #item.measurement_id="{ item, rowId, value }">
        {{ formMeasurements.find((m) => m.id === value)?.name }}
      </template>
      <template #item.phase="{ item, rowId, value }">
        <dhme-wkb-phase-chip :text="value?.substring(5)" :type="value" />
      </template>
      <template #item.q1="{ item, rowId, value }">
        <div class="d-flex flex-column my-1">
          <v-chip class="mb-1" x-small>Q: {{ item.q1 }}</v-chip>
          <v-chip x-small>A: {{ item.a1 }}</v-chip>
        </div>
      </template>
      <template #item.q2="{ item, rowId, value }">
        <div class="d-flex flex-column my-1">
          <v-chip class="mb-1" x-small>Q: {{ item.q2 }}</v-chip>
          <v-chip x-small>A: {{ item.a2 }}</v-chip>
        </div>
      </template>
      <template #item.q3="{ item, rowId, value }">
        <div class="d-flex flex-column my-1">
          <v-chip class="mb-1" x-small>Q: {{ item.q3 }}</v-chip>
          <v-chip x-small>A: {{ item.a3 }}</v-chip>
        </div>
      </template>
      <template #item.q4="{ item, rowId, value }">
        <div class="d-flex flex-column my-1">
          <v-chip class="mb-1" x-small>Q: {{ item.q4 }}</v-chip>
          <v-chip x-small>A: {{ item.a4 }}</v-chip>
        </div>
      </template>
      <template #item.q5="{ item, rowId, value }">
        <div class="d-flex flex-column my-1">
          <v-chip class="mb-1" x-small>Q: {{ item.q5 }}</v-chip>
          <v-chip x-small>A: {{ item.a5 }}</v-chip>
        </div>
      </template>
      <template #item.export_bg="{ item, rowId, value }">
        <v-simple-checkbox :value="!!value" disabled />
      </template>
      <template #item.export_warranty="{ item, rowId, value }">
        <v-simple-checkbox :value="!!value" disabled />
      </template>
      <template #item.export_contract="{ item, rowId, value }">
        <v-simple-checkbox :value="!!value" disabled />
      </template>
    </dynamic-data-table>
    <v-dialog
      key="wkb-form"
      v-model="wkbFormDialogDisplayed"
      max-width="600"
      @click:outside="closeWKBFormDialog"
      @keydown.esc="closeWKBFormDialog"
    >
      <v-card>
        <v-card-title class="justify-center text-uppercase headline">
          {{ wkbFormItem.id ? 'Update' : 'Add' }} Form
        </v-card-title>
        <v-divider />
        <div class="px-10 pb-5">
          <v-form ref="create-wkb-form-form" @submit.prevent>
            <ant-input label="Measurement">
              <template #input-field>
                <v-autocomplete
                  v-model="wkbFormItem.measurement_id"
                  :disabled="wkbLibraryTableStatus === 'loading'"
                  :items="formMeasurements"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  item-text="name"
                  item-value="id"
                  placeholder="Measurement"
                />
              </template>
            </ant-input>

            <div class="d-flex">
              <ant-input class="mr-5" label="Leanforms form" style="flex: 1">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.lf_form"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    :rules="[rules.required]"
                    dense
                    filled
                    hide-details
                    placeholder="Form ID"
                  />
                </template>
              </ant-input>

              <ant-input is-optional label="Leanforms project" style="flex: 2">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.lf_project"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Project"
                  />
                </template>
              </ant-input>
            </div>

            <ant-input label="Phase">
              <template #input-field>
                <v-autocomplete
                  v-model="wkbFormItem.phase"
                  :disabled="wkbLibraryTableStatus === 'loading'"
                  :items="phaseOptions()"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  item-text="name"
                  item-value="id"
                  placeholder="Phase"
                />
              </template>
            </ant-input>

            <div class="d-flex">
              <ant-input class="mr-5 flex-1" is-optional label="Q1">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.q1"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
              <ant-input class="flex-1" is-optional label="A1">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.a1"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
            </div>

            <div class="d-flex">
              <ant-input class="mr-5 flex-1" is-optional label="Q2">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.q2"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
              <ant-input class="flex-1" is-optional label="A2">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.a2"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
            </div>

            <div class="d-flex">
              <ant-input class="mr-5 flex-1" is-optional label="Q3">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.q3"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
              <ant-input class="flex-1" is-optional label="A3">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.a3"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
            </div>

            <div class="d-flex">
              <ant-input class="mr-5 flex-1" is-optional label="Q4">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.q4"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
              <ant-input class="flex-1" is-optional label="A4">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.a4"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
            </div>

            <div class="d-flex">
              <ant-input class="mr-5 flex-1" is-optional label="Q5">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.q5"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
              <ant-input class="flex-1" is-optional label="A5">
                <template #input-field>
                  <v-text-field
                    v-model="wkbFormItem.a5"
                    :disabled="wkbLibraryTableStatus === 'loading'"
                    dense
                    filled
                    hide-details
                    placeholder="Value"
                  />
                </template>
              </ant-input>
            </div>

            <div class="d-flex justify-center">
              <ant-input class="flex-1" is-optional label="Export BG">
                <template #input-field>
                  <v-checkbox
                    v-model="wkbFormItem.export_bg"
                    :value="false"
                    dense
                    hide-details
                  />
                </template>
              </ant-input>
              <ant-input
                class="mx-5 flex-1"
                is-optional
                label="Export Warranty"
              >
                <template #input-field>
                  <v-checkbox
                    v-model="wkbFormItem.export_warranty"
                    :value="false"
                    dense
                    hide-details
                  />
                </template>
              </ant-input>
              <ant-input class="flex-1" is-optional label="Export Contract">
                <template #input-field>
                  <v-checkbox
                    v-model="wkbFormItem.export_contract"
                    :value="false"
                    dense
                    hide-details
                  />
                </template>
              </ant-input>
            </div>
          </v-form>
        </div>
        <v-card-actions class="ant-border-top ant-dialog-actions-bg">
          <v-spacer />
          <v-btn
            :disabled="wkbLibraryTableStatus === 'loading'"
            class="mx-2"
            color="error"
            small
            text
            @click="closeWKBFormDialog"
            >cancel
          </v-btn>
          <v-btn
            :disabled="wkbLibraryTableStatus === 'loading'"
            :loading="wkbLibraryTableStatus === 'loading'"
            color="primary"
            small
            @click="saveOrUpdateWKBForm"
            >{{ wkbFormItem.id ? 'update' : 'create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <delete-dialog
      :dialog="wkbFormDeleteDialogDisplayed"
      title="Are you sure you want to delete this record?"
      @closeDialog="closeWKBFormDialog"
      @deleteAction="deleteWkbFormRecord"
    />
  </div>
</template>

<style lang="scss" scoped></style>
